import { useEffect, useMemo, useState, useCallback } from 'react';
import { Page, Header, Content, InfoCard } from '@backstage/core-components';
import { List, ListItem, ListItemText, Button, Grid, Typography, IconButton, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

type Entity = any;

type RepoInfo = {
  provider: 'gitlab' | 'unknown';
  host?: string;
  owner?: string;
  repo?: string;
  originalUrl?: string;
};

const useStyles = makeStyles(theme => ({
  card: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-start',
    background: theme.palette.background.paper,
    borderRadius: '4px',
    boxShadow: '0px 1px 3px rgba(0,0,0,0.08)',
    height: '100%',
    boxSizing: 'border-box' as const,
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-start',
    padding: '16px',
    gap: '8px',
    width: '100%',
    boxSizing: 'border-box' as const,
    flexGrow: 1,
  },
  cardLabel: {
    fontFamily: "'Arial', sans-serif",
    fontWeight: 700,
    fontSize: '12px',
    lineHeight: '16px',
    color: theme.palette.text.secondary,
    marginBottom: '4px',
  },
  cardHeadline: {
    fontFamily: "'Arial', sans-serif",
    fontWeight: 700,
    fontSize: '20px',
    lineHeight: '28px',
    color: theme.palette.text.primary,
    margin: '0 0 8px 0',
  },
  cardBody: {
    fontFamily: "'Arial', sans-serif",
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '20px',
    color: theme.palette.text.secondary,
    flexGrow: 1,
    margin: '0 0 16px 0',
  },
  cardLink: {
    display: 'inline-flex',
    flexDirection: 'row' as const,
    alignItems: 'center',
    gap: '4px',
    fontFamily: "'Arial', sans-serif",
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: '20px',
    color: theme.palette.primary.main,
    textDecoration: 'none',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  sectionLabel: {
    fontFamily: "'Arial', sans-serif",
    fontWeight: 700,
    fontSize: '16px',
    lineHeight: '20px',
    color: theme.palette.text.secondary,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    marginBottom: '8px',
  },
}));

type IdeCardProps = {
  label: string;
  title: string;
  description: string;
  linkLabel: string;
  onClick: () => void;
};

const IdeCard = ({ label, title, description, linkLabel, onClick }: IdeCardProps) => {
  const classes = useStyles();
  return (
    <div className={classes.card} style={{ width: '100%' }}>
      <div className={classes.cardContent}>
        <span className={classes.cardLabel}>{label}</span>
        <h2 className={classes.cardHeadline}>{title}</h2>
        <p className={classes.cardBody}>{description}</p>
        <button className={classes.cardLink} onClick={onClick} type="button">
          {linkLabel}
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path d="M8 5l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>
    </div>
  );
};

export const CoIdePage = () => {
  const catalogApi = useApi(catalogApiRef);
  const identityApi = useApi(identityApiRef);
  const [entities, setEntities] = useState<Entity[]>([]);
  const classes = useStyles();
  const [ideError, setIdeError] = useState<string | null>(null);

  const openIde = useCallback(async (url: string) => {
    setIdeError(null);
    try {
      const { token } = await identityApi.getCredentials();
      const resp = await fetch('/api/co-ide/grant-session', {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) {
        setIdeError('Could not establish a session. Please log in again.');
        return;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      setIdeError('Network error while starting session.');
    }
  }, [identityApi]);

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        const resp = await catalogApi.getEntities({
          fields: [
            'metadata.name',
            'metadata.title',
            'metadata.description',
            'metadata.annotations',
            'spec.owner',
            'spec.type',
          ],
          filter: { kind: 'Component' },
        });

        if (!mounted) return;
        setEntities(resp.items || []);
      } catch (e) {
        // ignore
      }
    }

    load();

    return () => {
      mounted = false;
    };
  }, [catalogApi]);

  const detectRepo = (entity: Entity): string | undefined => {
    const ann = entity?.metadata?.annotations || {};
    const keys = [
      'backstage.io/managed-by-location',
      'backstage.io/source-location',
      'backstage.io/techdocs-ref',
      'backstage.io/managed-by',
    ];

    for (const k of keys) {
      const v = ann[k];
      if (v && typeof v === 'string') {
        const match = v.match(/(https?:\/\/[^\s]+)/);
        if (match) return match[1];
        return v;
      }
    }

    if (entity?.spec?.owner) return undefined;
    return undefined;
  };

  const parseRepoInfo = (repoUrl: string | undefined): RepoInfo => {
    if (!repoUrl) return { provider: 'unknown' };

    const normalized = repoUrl.replace(/\.git$/, '');

    const gitSshMatch = normalized.match(/^git@([^:]+):(.+)$/);
    if (gitSshMatch) {
      const host = gitSshMatch[1];
      const path = gitSshMatch[2];
      const parts = path.split('/').filter(Boolean);
      const repo = parts.pop();
      const owner = parts.join('/');
      if (host.includes('gitlab') && owner && repo) {
        return { provider: 'gitlab', host, owner, repo, originalUrl: repoUrl };
      }
      return { provider: 'unknown', host, originalUrl: repoUrl };
    }

    try {
      const url = new URL(normalized);
      const host = url.host;
      const path = url.pathname.replace(/^\//, '');
      const parts = path.split('/').filter(Boolean);

      // For GitLab URLs, project path ends before the '/-/' segment (e.g. '/-/blob/...').
      // Find the index of the first '-' marker and only consider parts before it.
      let projectParts = parts;
      const dashIndex = parts.indexOf('-');
      if (dashIndex !== -1) {
        projectParts = parts.slice(0, dashIndex);
      }

      // Detect GitLab HTTP/HTTPS URLs: e.g. https://gitlab.example.com/group/project
      if (host.includes('gitlab') && projectParts.length >= 2) {
        const repo = projectParts.pop() as string;
        const owner = projectParts.join('/');
        return {
          provider: 'gitlab',
          host,
          owner,
          repo,
          originalUrl: repoUrl,
        };
      }

      return { provider: 'unknown', host, originalUrl: repoUrl };
    } catch {
      return { provider: 'unknown', originalUrl: repoUrl };
    }
  };

  const getGitlabIdeUrl = (repoUrl: string | undefined): string | undefined => {
    const info = parseRepoInfo(repoUrl);
    if (info.provider === 'gitlab' && info.host && info.owner && info.repo) {
      // Construct GitLab Web IDE URL using the project path form:
      // https://{host}/-/ide/project/{owner}/{repo}/edit
      // Support nested group/subgroup paths in `owner` by encoding each segment.
      const ownerPath = info.owner
        .split('/')
        .filter(Boolean)
        .map(encodeURIComponent)
        .join('/');
      const repoPart = encodeURIComponent(info.repo);
      return `https://${info.host}/-/ide/project/${ownerPath}/${repoPart}/edit`;
    }
    return undefined;
  };

  const listItems = useMemo(
    () => {
      const items = entities.map(entity => {
        const name = entity?.metadata?.name || 'unknown';
        const title = entity?.metadata?.title || name;
        const repo = detectRepo(entity);
        const vscodeUrl = getGitlabIdeUrl(repo);

        let repoRootUrl: string | undefined = undefined;
        try {
          const info = parseRepoInfo(repo);
          if (info.provider === 'gitlab' && info.host && info.owner && info.repo) {
            repoRootUrl = `https://${info.host}/${info.owner}/${info.repo}/`;
          } else if (repo && typeof repo === 'string') {
            // Fallback: try to strip any '/-/...' suffixes from the raw URL
            const u = new URL(repo);
            const parts = u.pathname.split('/').filter(Boolean);
            const dashIdx = parts.indexOf('-');
            const projectParts = dashIdx !== -1 ? parts.slice(0, dashIdx) : parts;
            if (projectParts.length >= 2) {
              const repoName = projectParts.pop();
              const owner = projectParts.join('/');
              repoRootUrl = `${u.protocol}//${u.host}/${owner}/${repoName}/`;
            }
          }
        } catch {
          // ignore
        }

        return {
          key: entity.metadata?.uid || name,
          name,
          title,
          repo,
          repoRootUrl,
          vscodeUrl,
        };
      });

      items.sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }));

      return items;
    },
    [entities],
  );

  return (
    <Page themeId="home">
      <Header title="co-IDE" />
      <Content>
        <Grid container spacing={3}>
          {/* IDE launchers */}
          <Grid item xs={12}>
            <p className={classes.sectionLabel}>Launch IDE</p>
            {ideError && (
              <Typography variant="caption" color="error" style={{ display: 'block', marginBottom: 8 }}>
                {ideError}
              </Typography>
            )}
            <Grid container spacing={2} alignItems="stretch">
              <Grid item md={4} xs={12} style={{ display: 'flex' }}>
                <IdeCard
                  label="IDE"
                  title="VS Code Server"
                  description="Open a browser-based VS Code editor backed by the co-IDE VS Code Server container."
                  linkLabel="Open VS Code"
                  onClick={() => openIde(`${window.location.origin}/coide-vscode/`)}
                />
              </Grid>
              <Grid item md={4} xs={12} style={{ display: 'flex' }}>
                <IdeCard
                  label="IDE"
                  title="Eclipse Theia"
                  description="Open a browser-based Eclipse Theia IDE backed by the co-IDE Theia container."
                  linkLabel="Open Theia"
                  onClick={() => openIde(`${window.location.origin}/coide-theia/`)}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <InfoCard title="Available Repositories">
              <Typography variant="body2" paragraph>
                Select a repository to launch in the web-based co-IDE.
              </Typography>
              <List>
                {entities.length === 0 && (
                  <ListItem>
                    <ListItemText primary="No repositories found in the catalog." />
                  </ListItem>
                )}
                {listItems.map(item => (
                  <ListItem key={item.key} divider>
                    <ListItemText
                      primary={<Typography variant="h6">{item.title}</Typography>}
                      secondary={
                        item.repoRootUrl ? (
                          <div>
                            <Typography variant="body2">Name: {item.name}</Typography>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ marginRight: 6 }}>Link:</span>
                              <a href={item.repoRootUrl} target="_blank" rel="noreferrer">
                                {item.repoRootUrl}
                              </a>
                              <Tooltip title="Copy link">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    const text = item.repoRootUrl || '';
                                    if (navigator.clipboard && navigator.clipboard.writeText) {
                                      navigator.clipboard.writeText(text).catch(() => {});
                                    } else {
                                      const ta = document.createElement('textarea');
                                      ta.value = text;
                                      document.body.appendChild(ta);
                                      ta.select();
                                      try {
                                        document.execCommand('copy');
                                      } catch {
                                        // ignore
                                      }
                                      document.body.removeChild(ta);
                                    }
                                  }}
                                  aria-label="Copy repository link"
                                >
                                  <FileCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </div>
                          </div>
                        ) : (
                          <Typography variant="body2">
                            {item.repo || 'Repository URL not available in entity annotations'}
                          </Typography>
                        )
                      }
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={!item.vscodeUrl}
                      onClick={() => {
                        if (item.vscodeUrl) window.open(item.vscodeUrl, '_blank');
                      }}
                    >
                      Open in GitLab Web IDE
                    </Button>
                  </ListItem>
                ))}
              </List>
            </InfoCard>
          </Grid>
        </Grid>
      </Content>
    </Page>
  );
};

export default CoIdePage;
